/**
 * Algeria time, for the dates people type and read (an appel's opening and closing).
 *
 * The API takes an ISO instant and stores UTC; people think in Algeria time, which is UTC+1 all
 * year (no daylight saving). A date and a time typed on screen are therefore sent with the fixed
 * `+01:00` offset, and an instant read back is shown as the Algeria date and time, whatever the
 * browser's own time zone is. One place, so no screen does its own arithmetic.
 */

export const ALGIERS_TIME_ZONE = "Africa/Algiers";

/** A calendar date (`2026-11-15`) and a 24-hour time (`09:00`), as typed. */
export interface DateAndTime {
  date: string;
  time: string;
}

/**
 * `2026-11-15` and `09:00` as the instant the API wants: `2026-11-15T09:00:00+01:00`. Returns
 * `null` when either part is missing or not in the expected shape, so a form can treat it as
 * "not filled in".
 */
export function toAlgiersIso(parts: DateAndTime): string | null {
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(parts.date);
  const timeOk = /^([01]\d|2[0-3]):[0-5]\d$/.test(parts.time);
  return dateOk && timeOk ? `${parts.date}T${parts.time}:00+01:00` : null;
}

/**
 * An ISO instant (any offset, usually UTC) as the Algeria date and time. Returns empty parts for a
 * missing or unparsable value.
 */
export function fromAlgiersIso(iso: string | null | undefined): DateAndTime {
  if (!iso) return { date: "", time: "" };
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return { date: "", time: "" };

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ALGIERS_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instant);
  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";

  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour")}:${get("minute")}`,
  };
}
